import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { ProviderType } from '../../models/ProviderDetails';
import {
  CareTeamState,
  StoreKeys,
  careTeamRecords,
  initialState,
} from './careTeamStore';

// Define the store logic
const store = (set: any, get: any): CareTeamState => ({
  ...initialState,
  isCallDone: false,
  bookmarkedProviders: [],
  careTeamProviderCount: 0, // Initialize as a number
  showMaximumProviderLimitBanner: false, // Initialize as false
  providerDetails: {
    providerName: '',
    providerType: ProviderType.PRACTITIONER,
    providerId: '',
    providerLocations: [],
  },

  setShowMaximumProviderLimitBanner: (show: boolean) => {
    set(() => ({
      showMaximumProviderLimitBanner: show, // Update banner visibility
    }));
  },

  clearShowMaximumProviderLimitBanner: () => {
    set(() => ({
      showMaximumProviderLimitBanner: false, // Reset banner visibility to false
    }));
  },

  setCareTeamProviderCount: () => {
    const count = get().bookmarkedProviders.length;
    set(() => ({
      careTeamProviderCount: count, // Update the count dynamically
    }));
  },

  [StoreKeys.SET_CARE_TEAM_RECORDS]: (data: {}) => {
    set((state) => ({
      [StoreKeys.CARE_TEAM_RECORDS]: {
        ...state[StoreKeys.CARE_TEAM_RECORDS],
        ...data,
      },
    }));
  },

  [StoreKeys.CLEAR_CARE_TEAM_RECORDS]: (
    defaultValues: careTeamRecords = initialState[StoreKeys.CARE_TEAM_RECORDS]
  ) => {
    set(() => ({
      [StoreKeys.CARE_TEAM_RECORDS]: defaultValues,
    }));
  },

  [StoreKeys.SET_BOOKMARKED_PROVIDERS]: (providerIds: string[]) => {
    set(() => ({
      [StoreKeys.BOOKMARKED_PROVIDERS]: providerIds || [],
    }));
    get().setCareTeamProviderCount(); // Update count after setting providers
  },

  [StoreKeys.ADD_BOOKMARK]: (providerId: string) => {
    set((state) => {
      if (state.bookmarkedProviders.includes(providerId)) {
        return state;
      }
      return {
        bookmarkedProviders: [...state.bookmarkedProviders, providerId],
      };
    });
    get().setCareTeamProviderCount(); // Update count after adding a bookmark
  },

  [StoreKeys.REMOVE_BOOKMARK]: (providerId: string) => {
    set((state: CareTeamState) => ({
      bookmarkedProviders: state.bookmarkedProviders.filter(
        (id) => id !== providerId
      ),
    }));
    get().setCareTeamProviderCount(); // Update count after removing a bookmark
  },

  [StoreKeys.IS_BOOKMARKED]: (providerId: string) =>
    get().bookmarkedProviders.includes(providerId),

  [StoreKeys.SET_PROVIDER_DETAILS_FOR_DIRECTION]: (data: Object) => {
    set(() => ({
      [StoreKeys.PROVIDER_DETAILS_FOR_DIRECTION]: data,
    }));
  },
  [StoreKeys.CLEAR_PROVIDER_DETAILS_FOR_DIRECTION]: () => {
    set(() => ({
      [StoreKeys.PROVIDER_DETAILS_FOR_DIRECTION]: {},
    }));
  },
  setIsCallDone: (done: boolean) => {
    set(() => ({
      isCallDone: done,
    }));
  },
});

// Apply persist middleware
const careTeamStore = persist(store, {
  name: 'useCareTeamStore',
  storage: createJSONStorage(() => sessionStorage),
});

// Create the store with devtools and persist
export const useCareTeamStore = create(devtools(careTeamStore));
